import { transition } from '@helpers/animate';
import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import fonts from '@helpers/fonts';
import { Device, from, until } from '@helpers/media';
import ButtonStyles from '@stories/Components/Buttons/Button/Button.styles';
import SectionWrapper from '@stories/Components/Misc/SectionWrapper/SectionWrapper';
import SectionWrapperStyles from '@stories/Components/Misc/SectionWrapper/SectionWrapper.styles';
import { m } from 'framer-motion';
import styled, { createGlobalStyle } from 'styled-components';
export const HeaderGlobalStyles = createGlobalStyle `
  html {
    padding-top: var(--headerHeight);
  }
`;
const Container = styled.nav `
  position: fixed;
  height: var(--headerHeight);
  inset: 0;
  width: 100%;
  z-index: 103;

  display: flex;
  flex-direction: column;
`;
const Inner = styled(SectionWrapper) `
  height: 100%;
  z-index: 100;

  ${SectionWrapperStyles.Inner} {
    max-width: 1920px;
    padding: 0px ${fluid(20, 32)};
    display: flex;
    justify-content: space-between;
    align-items: center;
    height: 100%;
  }

  && {
    --sectionBackgroundColor: ${brand.primary.brightBlue};
    --sectionForegroundColor: ${brand.primary.white};
  }
`;
const Logo = styled.div `
  max-width: ${fluid(120, 170, Device.Mobile, Device.ActualDesktop)};
  max-height: 73px;

  svg {
    width: 100%;
    height: 100%;
  }
`;
const CtasWrapper = styled.div `
  display: flex;
  align-items: center;
  gap: 10px;

  @media ${until(Device.TabletLarge)} {
    display: none;
  }
`;
const NavigationSectionsWrapper = styled.div `
  display: flex;
  align-items: center;
  gap: 24px;

  ${fonts.ubuntu.regular};

  @media ${until(Device.TabletLarge)} {
    display: none;
  }
`;
const NavigationSection = styled(m.div).attrs((props) => ({
    initial: 'default',
    animate: props['data-active'] ? 'active' : 'default',
    variants: {
        default: {
            '--rotate': '90deg',
        },
        active: {
            '--rotate': '-90deg',
        },
    },
    transition: {
        duration: 0.35,
    },
})) `
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  user-select: none;

  a {
    color: ${brand.secondary.lightBlue};

    &:hover {
      color: ${brand.secondary.paleBlue};
    }

    ${transition('color', '200ms', 'cubic-bezier(.165,.84,.44,1)')};
  }

  font-size: 20px;
  line-height: 24px;
  color: ${brand.secondary.lightBlue};

  &[data-active='true'],
  &:hover {
    color: ${brand.secondary.paleBlue};
  }

  ${transition('color', '200ms', 'cubic-bezier(.165,.84,.44,1)')};
`;
const NavigationArrow = styled.div `
  display: flex;
  width: 8px;
  height: 13px;
  transform: rotate(var(--rotate));

  svg {
    width: 100%;
    height: 100%;
  }
`;
const MegaMenu = styled(m.div).attrs((props) => ({
    initial: 'hidden',
    animate: props['data-active'] ? 'visible' : 'hidden',
    exit: 'hidden',
    variants: {
        hidden: {
            y: '-100%',
            transition: {
                duration: 0.25,
                ease: 'easeIn',
            },
        },
        visible: {
            y: '0%',
        },
    },
    transition: {
        bounce: 0,
        duration: 0.5,
        ease: 'easeOut',
    },
})) `
  @media ${until(Device.TabletLarge)} {
    display: none;
  }

  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  background-color: ${brand.primary.darkBlue};
  padding: 16px 0px 45px 0px;

  display: flex;
  justify-content: center;
  align-items: center;

  &[data-active='true'] {
    z-index: 99;
  }
  z-index: 98;
`;
const MegaMenuInner = styled.div `
  display: flex;
  gap: 15px;

  --sectionForegroundColor: ${brand.secondary.lightBlue};
`;
const MegaMenuBackground = styled(m.div).attrs(() => ({
    initial: 'hidden',
    animate: 'visible',
    exit: 'hidden',
    variants: {
        hidden: {
            opacity: 0,
            pointerEvents: 'none',
        },
        visible: {
            opacity: 1,
            pointerEvents: 'unset',
        },
    },
})) `
  position: fixed;
  height: 100dvh;
  width: 100dvw;

  z-index: 101;
  @media ${from(Device.TabletLarge)} {
    z-index: 97;
  }

  background: rgba(0, 0, 0, 0.5);
`;
const MegaMenuSection = styled.div `
  display: flex;
  flex-direction: column;
  gap: 0;
  height: max-content;
  max-height: 100%;

  &[data-show-divider='true'] {
    border-right: 1px solid ${brand.primary.white};
    padding-right: 20px;
    margin-right: 10px;
  }

  h4 {
    font-size: 18px;
    line-height: 24px;
    color: ${brand.secondary.lightBlue};
    margin-bottom: 5px;

    ${fonts.ubuntu.medium};
  }

  a {
    ${ButtonStyles.Text} {
      text-decoration: none;
      line-height: 24px;
      padding: 4px 0;
    }

    width: max-content;
    max-width: 100%;

    ${ButtonStyles.IconWrapper} {
      width: 13px;
      height: 13px;
    }

    svg {
      color: ${brand.primary.brightBlue};
    }

    &:first-child {
      padding-bottom: 5px;
    }
  }
`;
const BurgerMenuIcon = styled.div `
  width: ${fluid(60, 74, Device.Mobile, Device.ActualDesktop)};
  height: 43px;
  cursor: pointer;

  @media ${from(Device.TabletLarge)} {
    display: none;
  }

  svg {
    width: 100%;
    height: 100%;
  }
`;
const MobileMenu = styled(m.div).attrs(() => ({
    initial: 'hidden',
    animate: 'visible',
    exit: 'hidden',
    variants: {
        hidden: {
            '--x': '100%',
            '--opacity': 0,
        },
        visible: {
            '--x': '0%',
            '--opacity': 1,
        },
    },
    transition: {
        bounce: 0,
    },
})) `
  position: fixed;
  top: 0;
  right: 0;
  height: 100dvh;
  padding: ${fluid(20, 32, Device.Mobile)};
  padding-top: 0px;
  min-width: 330px;
  background-color: ${brand.primary.white};
  z-index: 102;

  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  opacity: var(--opacity);

  @media ${until(Device.Tablet)} {
    width: 100dvw;
    transform: unset !important;
  }

  @media ${from(Device.Tablet)} {
    opacity: 1 !important;
    transform: translateX(var(--x)) !important;
  }

  @media ${from(Device.TabletLarge)} {
    display: none;
  }
`;
const MobileMenuHeader = styled.div `
  height: var(--headerHeight);
  padding-bottom: 20px;
  margin-bottom: 24px;

  border-bottom: 1px solid ${brand.grey.base};

  display: flex;
  justify-content: space-between;
  align-items: flex-end;

  img {
    max-width: ${fluid(122, 195, Device.Mobile, Device.Tablet)};
    @media ${until(Device.Tablet)} {
      padding-bottom: 17px;
    }
  }

  svg {
    cursor: pointer;
    color: ${brand.primary.brightBlue};
    width: 36px;
    height: 36px;
    flex-shrink: 0;

    margin-bottom: 10px;
  }
`;
const MobileNavigationSection = styled.ul `
  display: flex;
  flex-direction: column;
  gap: 16px;

  padding-left: calc(${(props) => props['data-level']} * 32px);
`;
const MobileNavigationItem = styled.li `
  ${fonts.ubuntu.regular};
  font-size: 20px;
  line-height: 24px;
  color: ${brand.primary.darkBlue};
  display: flex;
  user-select: none;
  flex-direction: column;
  width: 100%;

  &[data-has-collapse='true'] {
    > span {
      cursor: pointer;
    }
  }

  &[data-active='true'] {
    svg {
      transform: rotate(90deg);
    }
  }

  &[data-is-heading='true'] {
    ${fonts.ubuntu.bold};

    ${ButtonStyles.Text} {
      ${fonts.ubuntu.bold};
    }
  }

  span {
    display: flex;
    align-items: center;
    gap: 10px;

    &[data-light-blue='true'] {
      ${fonts.ubuntu.regular};
      color: ${brand.primary.brightBlue};
    }
  }

  svg {
    width: 10px;
    color: ${brand.primary.brightBlue};

    ${transition('transform', '200ms', 'cubic-bezier(.165,.84,.44,1)')};
  }

  a {
    color: ${brand.primary.darkBlue};
    text-decoration: none;

    ${ButtonStyles.Text} {
      text-decoration: none;
    }

    justify-content: flex-start;

    ${ButtonStyles.IconWrapper} {
      width: 13px;
      height: 13px;
    }

    svg {
      color: ${brand.primary.brightBlue};
    }
  }
`;
const CollapsibleSection = styled(m.div).attrs((props) => ({
    initial: 'collapsed',
    animate: props['data-expanded'] ? 'expanded' : 'collapsed',
    variants: {
        collapsed: {
            height: 0,
        },
        expanded: {
            height: 'auto',
        },
    },
    transition: {
        duration: 0.35,
        bounce: 0,
    },
})) `
  overflow: hidden;

  > ${MobileNavigationSection} {
    padding-top: 14px;
  }
`;
const HeaderStyles = {
    Container,
    Logo,
    Inner,
    CtasWrapper,
    NavigationSectionsWrapper,
    NavigationSection,
    NavigationArrow,
    MegaMenu,
    MegaMenuBackground,
    MegaMenuInner,
    MegaMenuSection,
    BurgerMenuIcon,
    MobileMenu,
    MobileMenuHeader,
    MobileNavigationSection,
    MobileNavigationItem,
    CollapsibleSection,
};
export default HeaderStyles;
